/**
 * ClientHome — client dashboard for the web PWA (returning client).
 *
 * Shows all of the client's applications in a single scannable list, each with
 * an inline progress bar. Clicking an application navigates to its document
 * checklist. A "New application" button opens the inline service picker.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  CalendarClock,
  ChevronDown,
  Clock,
  FileText,
  Key,
  KeyRound,
  Landmark,
  Loader2,
  Mic,
  Plus,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

import { ApiClientError, dossierService } from "@re-platform/api-client";
import type { DossierSummary } from "@re-platform/types";
import { Button, StatusPill, type StatusTone } from "@re-platform/ui";

import { api } from "../../lib/api";
import {
  acceptProposedTime,
  cancelAppointment,
  listMyAppointments,
  rescheduleAppointment,
  type AppointmentRequest,
} from "../appointments/appointmentApi";
import {
  COMMUNITY_PROPERTY_SERVICES,
  EMPTY_LISTING_ANSWERS,
  isListingService,
  type ListingAnswers,
  ListingDetailsFields,
  listingProblems,
  listingRequestBody,
  type Service,
  withSuggestedTerm,
} from "./listingDetails";
import { useBraStatus, usePreparingDocuments } from "../trec/useBraStatus";
import { listMyFollowups, type FollowupRequest } from "../voice/voiceApi";
import "./ClientHome.css";
// The shared listing fields use the wizard's co-* field styles.
import "./ClientOnboarding.css";

// ---- Service picker (for "new application") ----------------------------------

const SERVICES: Array<{
  id: Service;
  icon: typeof FileText;
  label: string;
  description: string;
}> = [
  {
    id: "buy",
    icon: FileText,
    label: "Buy a Home",
    description: "Purchase — own financing or cash.",
  },
  {
    id: "buy_loan",
    icon: Landmark,
    label: "Buy + Get a Mortgage",
    description: "Need buyer's agent AND financing.",
  },
  {
    id: "rent",
    icon: Key,
    label: "Rent a Home",
    description: "Tenant application + showing.",
  },
  {
    id: "sell",
    icon: Building2,
    label: "Sell My Home",
    description: "Seller listing + disclosure docs.",
  },
  {
    id: "lease",
    icon: KeyRound,
    label: "Lease Out My Property",
    description: "Landlord listing + lease agreement.",
  },
  {
    id: "mortgage",
    icon: FileText,
    label: "Get Pre-Approved",
    description: "Standalone mortgage pre-approval.",
  },
];

function NewApplicationPanel({ onClose }: { onClose: () => void }) {
  const [selected, setSelected] = useState<Service | null>(null);
  const [listing, setListing] = useState<ListingAnswers>(EMPTY_LISTING_ANSWERS);
  // Only asked for a service that conveys a property interest, and only because
  // the client may not have answered it on a previous application. Left blank,
  // whatever they told us before still binds — the server guards on the stored
  // row, not on this field.
  const [maritalStatus, setMaritalStatus] = useState("");
  const [spouseFirst, setSpouseFirst] = useState("");
  const [spouseLast, setSpouseLast] = useState("");
  const [spouseEmail, setSpouseEmail] = useState("");
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Only what this client's AGENT can actually service. The onboarding wizard
  // has always filtered on this; this panel rendered the full hardcoded list,
  // so a mortgage broker's client was offered "Buy a Home" and "Sell" — TREC
  // work that brokerage cannot do and that nobody there could have finished.
  const optionsQuery = useQuery({
    queryKey: ["onboarding-options", "my-agent"],
    queryFn: () =>
      api.request<{
        agent_user_id: string | null;
        offered_services: string[];
        services_in_progress: string[];
        listing_term_months: number | null;
      }>("/document/onboarding-options"),
    staleTime: 300_000,
  });
  const offered = optionsQuery.data?.offered_services;
  const tiles = offered
    ? SERVICES.filter((s) => offered.includes(s.id))
    : SERVICES;
  // Already open. Picking one again was never destructive — self-onboard hands
  // the existing dossier back — but it looked like nothing happened, which is
  // worse than saying so. Freed up when that application is cancelled or done.
  const inProgress = new Set(optionsQuery.data?.services_in_progress ?? []);

  const mutation = useMutation({
    mutationFn: (service: Service) => {
      // The id the SERVER resolved, not our localStorage copy — that may hold
      // an agents(id) from a landing URL, or a ref that no longer exists.
      const agentId = optionsQuery.data?.agent_user_id;
      return api.request<{
        dossier_id: string;
        client_id: string;
        application_number: number;
      }>("/document/self-onboard", {
        method: "POST",
        body: {
          service,
          ...(agentId ? { assigned_agent_id: agentId } : {}),
          // ¶2A/¶3/¶4A. Missed when the wizard step landed, so a RETURNING
          // client leased out a property and was never asked its address, rent
          // or term — the agreement then had nothing to print.
          ...listingRequestBody(service, listing),
          ...(maritalStatus ? { marital_status: maritalStatus } : {}),
          ...(maritalStatus === "married"
            ? {
                co_client_first_name: spouseFirst.trim(),
                co_client_last_name: spouseLast.trim(),
                co_client_email: spouseEmail.trim(),
              }
            : {}),
        },
      });
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["my-dossiers"] });
      onClose();
      // Land ON the new application, not back where they started. The panel
      // used to close and leave the client on the home page, which looks
      // exactly like nothing happened — the same defect #141 fixed in the
      // onboarding wizard, in the component it did not touch.
      navigate(`/dossier/${created.dossier_id}`);
    },
  });

  return (
    <div className="ch-panel">
      <div className="ch-panel__head">
        <h3>Start a new application</h3>
        <button
          className="ch-panel__close"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>
      <p className="ch-panel__sub">
        Pick the service you need — we'll create a document checklist
        automatically.
      </p>
      <div className="ch-panel__services">
        {tiles.map((svc) => {
          const Icon = svc.icon;
          const isSelected = selected === svc.id;
          const started = inProgress.has(svc.id);
          return (
            <button
              key={svc.id}
              className={`ch-svc ${isSelected ? "ch-svc--selected" : ""}`}
              onClick={() => {
                if (started) return;
                setSelected(svc.id);
                if (isListingService(svc.id)) {
                  setListing((prev) =>
                    withSuggestedTerm(
                      prev,
                      optionsQuery.data?.listing_term_months,
                    ),
                  );
                }
              }}
              disabled={mutation.isPending || started}
              title={
                started
                  ? "You already have this application open — cancel or finish it first."
                  : undefined
              }
            >
              <Icon size={18} strokeWidth={2} />
              <div>
                <div className="ch-svc__label">{svc.label}</div>
                <div className="ch-svc__desc">
                  {started ? "Already in progress" : svc.description}
                </div>
              </div>
              {isSelected && <span className="ch-svc__check">✓</span>}
            </button>
          );
        })}
      </div>
      {selected && COMMUNITY_PROPERTY_SERVICES.has(selected) && (
        <div className="ch-panel__listing">
          {/* Texas is a community property state: a married client's spouse has
              an interest in what is bought, sold or leased out and signs the
              agreement too. Blank means "as before" — the server reads the
              stored answer, so a client who already told us stays told. */}
          <label className="co-field">
            <span>Marital status</span>
            <select
              value={maritalStatus}
              onChange={(e) => setMaritalStatus(e.target.value)}
            >
              <option value="">Same as last time</option>
              <option value="married">Married</option>
              <option value="unmarried">Unmarried</option>
              <option value="separated">Separated</option>
            </select>
          </label>
          {maritalStatus === "married" && (
            <>
              <div className="co-field-row">
                <label className="co-field">
                  <span>Spouse's first name</span>
                  <input
                    value={spouseFirst}
                    onChange={(e) => setSpouseFirst(e.target.value)}
                  />
                </label>
                <label className="co-field">
                  <span>Spouse's last name</span>
                  <input
                    value={spouseLast}
                    onChange={(e) => setSpouseLast(e.target.value)}
                  />
                </label>
              </div>
              {/* Where DocuSign sends their signing link — no email, no
                  signature, no enforceable agreement. */}
              <label className="co-field">
                <span>Spouse's email</span>
                <input
                  type="email"
                  value={spouseEmail}
                  onChange={(e) => setSpouseEmail(e.target.value)}
                />
              </label>
            </>
          )}
        </div>
      )}
      {isListingService(selected) && (
        <div className="ch-panel__listing">
          <p className="ch-panel__sub">
            This goes on your listing agreement. If you're not sure of something
            yet, leave it — your agent will finish it with you before you sign.
          </p>
          <ListingDetailsFields
            service={selected}
            answers={listing}
            onChange={setListing}
          />
        </div>
      )}
      {mutation.isError && (
        <div className="ch-panel__err">
          {mutation.error instanceof ApiClientError
            ? mutation.error.message
            : "Something went wrong. Please try again."}
        </div>
      )}
      <div className="ch-panel__actions">
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={
            !selected || mutation.isPending || listingProblems(listing).any
          }
          onClick={() => selected && mutation.mutate(selected)}
        >
          {mutation.isPending ? (
            <>
              <Loader2 size={14} className="ch-spin" aria-hidden /> Creating…
            </>
          ) : (
            "Start application →"
          )}
        </Button>
      </div>
    </div>
  );
}

// ---- Application list --------------------------------------------------------

const TX_LABEL: Record<DossierSummary["transaction_type"], string> = {
  mortgage_application: "Mortgage application",
  showing: "Property showing",
  listing: "Listing",
};

const STATUS_TONE: Record<DossierSummary["status"], StatusTone> = {
  PENDING: "neutral",
  IN_PROGRESS: "info",
  COMPLETE: "success",
  EXPIRED: "warning",
  CANCELLED: "neutral",
};

function statusLabel(status: DossierSummary["status"]): string {
  return status.toLowerCase().replace("_", " ");
}

function AppRow({ d }: { d: DossierSummary }) {
  const total = d.slots_total;
  const done = d.slots_validated;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const hasProgress = total > 0;

  return (
    <Link
      to={`/dossier/${d.id}`}
      className="ch-app-row"
      data-status={d.status}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <div className="ch-app-row__top">
        <div className="ch-app-row__name">
          {d.template_display_name ?? TX_LABEL[d.transaction_type]}
          {d.application_number != null && (
            <span className="ch-past__appno">APP-{d.application_number}</span>
          )}
        </div>
        <StatusPill tone={STATUS_TONE[d.status]}>
          {statusLabel(d.status)}
        </StatusPill>
      </div>
      <div className="ch-app-row__sub">{TX_LABEL[d.transaction_type]}</div>
      {hasProgress && (
        <div className="ch-app-row__progress">
          <div className="ch-app-bar">
            <div className="ch-app-bar__fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="ch-app-row__counts">
            {done} / {total} documents
          </span>
        </div>
      )}
      {/* What to DO next, under how far along they are. A count answers
          "am I nearly there"; this answers "what do you need from me". */}
      {d.next_document && (
        <p className="ch-app-row__next">
          <Clock size={14} aria-hidden />
          Next: {d.next_document}
        </p>
      )}
    </Link>
  );
}

// ---- "My requests" (voice-assistant followups + their status) -----------------

const FOLLOWUP_TONE: Record<FollowupRequest["status"], StatusTone> = {
  NEW: "info",
  CONTACTED: "success",
  CLOSED: "neutral",
};

const FOLLOWUP_LABEL: Record<FollowupRequest["status"], string> = {
  NEW: "Requested",
  CONTACTED: "Agent contacted you",
  CLOSED: "Closed",
};

const TOPIC_LABEL: Record<string, string> = {
  appointment: "Appointment",
  callback: "Callback",
  question: "Question",
  other: "Request",
};

function MyRequests() {
  const followupsQuery = useQuery({
    queryKey: ["my-followups"],
    queryFn: () => listMyFollowups(),
    refetchInterval: 60_000,
  });
  const followups = followupsQuery.data ?? [];
  if (followups.length === 0) return null;

  return (
    <section className="ch-requests">
      <h2>My requests &amp; questions</h2>
      <p className="ch-requests__sub">
        Things you asked the assistant to pass to your agent, and where each one
        stands.
      </p>
      <div className="ch-requests__list">
        {followups.map((f) => (
          <div key={f.id} className="ch-request">
            <div className="ch-request__top">
              <span className="ch-request__topic">
                {TOPIC_LABEL[f.topic] ?? "Request"}
              </span>
              <StatusPill tone={FOLLOWUP_TONE[f.status]}>
                {FOLLOWUP_LABEL[f.status]}
              </StatusPill>
            </div>
            <div className="ch-request__details">{f.details}</div>
            {f.agent_reply && (
              <div className="ch-request__reply">
                <span className="ch-request__reply-label">Agent replied: </span>
                {f.agent_reply}
              </div>
            )}
            <div className="ch-request__meta">
              {f.preferred_time && (
                <span>Preferred: {f.preferred_time} · </span>
              )}
              Asked {new Date(f.created_at).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---- "My showing requests" (property showing requests + their status) --------

const APPT_TONE: Record<string, StatusTone> = {
  PENDING: "info",
  PROPOSED: "warning",
  CONFIRMED: "success",
  COMPLETED: "success",
  CANCELLED: "danger",
};

const APPT_LABEL: Record<string, string> = {
  PENDING: "Awaiting confirmation",
  PROPOSED: "New time proposed by your agent",
  CONFIRMED: "Confirmed",
  CANCELLED: "Cancelled",
  COMPLETED: "Showing done",
};

/** Statuses a client can still act on. */
const APPT_LIVE = new Set(["PENDING", "PROPOSED", "SCHEDULED", "CONFIRMED"]);

/** `<input type="datetime-local">` wants a local-time string, not an ISO/UTC one. */
function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function hoursUntil(iso: string): number {
  return (new Date(iso).getTime() - Date.now()) / 3_600_000;
}

function ViewingActions({ appt }: { appt: AppointmentRequest }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [when, setWhen] = useState(() => toLocalInputValue(appt.starts_at));
  const [error, setError] = useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["my-appointments"] });
  const fail = (e: unknown) =>
    setError(
      e instanceof Error ? e.message : "That didn't work — please try again.",
    );

  const reschedule = useMutation({
    mutationFn: () =>
      rescheduleAppointment(appt.id, new Date(when).toISOString()),
    onSuccess: () => {
      setEditing(false);
      setError(null);
      refresh();
    },
    onError: fail,
  });
  const accept = useMutation({
    mutationFn: () => acceptProposedTime(appt.id),
    onSuccess: refresh,
    onError: fail,
  });
  const cancel = useMutation({
    mutationFn: () => cancelAppointment(appt.id),
    onSuccess: refresh,
    onError: fail,
  });

  if (!APPT_LIVE.has(appt.status)) return null;

  // Warn BEFORE they act, not after: changing a confirmed showing inside 24h
  // counts against them, and three in a row pause their ability to book.
  const isLate = appt.status === "CONFIRMED" && hoursUntil(appt.starts_at) < 24;
  const busy = reschedule.isPending || accept.isPending || cancel.isPending;

  return (
    <div className="ch-request__actions">
      {editing ? (
        <>
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            aria-label="New viewing time"
          />
          <button
            type="button"
            onClick={() => reschedule.mutate()}
            disabled={busy}
          >
            {busy ? "Saving…" : "Propose this time"}
          </button>
          <button type="button" onClick={() => setEditing(false)}>
            Never mind
          </button>
        </>
      ) : (
        <>
          {appt.status === "PROPOSED" && (
            <button
              type="button"
              className="ch-btn--accept"
              onClick={() => accept.mutate()}
              disabled={busy}
            >
              Accept this time
            </button>
          )}
          <button
            type="button"
            onClick={() => setEditing(true)}
            disabled={busy}
          >
            Reschedule
          </button>
          <button
            type="button"
            className="ch-btn--danger"
            onClick={() => cancel.mutate()}
            disabled={busy}
          >
            Cancel
          </button>
        </>
      )}
      {isLate && (
        <p className="ch-request__warn">
          This showing is less than 24 hours away — changing it now counts as a
          late change. Three in a row and you'll need your agent to re-enable
          bookings.
        </p>
      )}
      {error && <p className="ch-request__warn">{error}</p>}
    </div>
  );
}

/**
 * Next showing first; finished ones sink.
 *
 * A client opening this card wants one answer: "when am I next seeing a house?"
 * So live viewings (anything still actionable) sort SOONEST-first — the next one
 * is the top row — and COMPLETED/CANCELLED drop below them, most-recent-first,
 * where they read as history rather than competing for attention.
 */
function byNextFirst(a: AppointmentRequest, b: AppointmentRequest): number {
  const aLive = APPT_LIVE.has(a.status);
  const bLive = APPT_LIVE.has(b.status);
  if (aLive !== bLive) return aLive ? -1 : 1;

  const at = new Date(a.starts_at).getTime();
  const bt = new Date(b.starts_at).getTime();
  // Live: soonest first (the next showing). Done: most recent first.
  return aLive ? at - bt : bt - at;
}

function MyViewingRequests() {
  const query = useQuery({
    queryKey: ["my-appointments"],
    queryFn: () => listMyAppointments(),
    refetchInterval: 60_000,
  });
  const appts: AppointmentRequest[] = [...(query.data ?? [])].sort(byNextFirst);
  if (appts.length === 0) return null;

  return (
    <section className="ch-requests">
      <h2>My showing requests</h2>
      <p className="ch-requests__sub">
        Property showings you asked for, and where each one stands.
      </p>
      <div className="ch-requests__list">
        {appts.map((a) => (
          <div key={a.id} className="ch-request">
            <div className="ch-request__top">
              <span className="ch-request__topic">
                {a.property_address ?? "Property showing"}
              </span>
              <StatusPill tone={APPT_TONE[a.status] ?? "neutral"}>
                {APPT_LABEL[a.status] ?? a.status}
              </StatusPill>
            </div>
            <div className="ch-request__meta">
              {a.status === "PROPOSED" ? "Proposed" : "Preferred"}:{" "}
              {new Date(a.starts_at).toLocaleString()}
              {a.agent_name ? ` · Agent: ${a.agent_name}` : ""}
            </div>
            <ViewingActions appt={a} />
          </div>
        ))}
      </div>
    </section>
  );
}

// ---- Main component ----------------------------------------------------------

export function ClientHome() {
  const navigate = useNavigate();
  const [showNewApp, setShowNewApp] = useState(false);

  const dossiersQuery = useQuery({
    queryKey: ["my-dossiers"],
    queryFn: () => dossierService.list(api, { limit: 50 }),
    refetchInterval: 30_000,
  });

  const dossiers = dossiersQuery.data ?? [];
  const bra = useBraStatus();
  const preparing = usePreparingDocuments();
  // The client owes a signature RIGHT NOW. Not "has ever had a BRA": a client
  // who signed one on a previous application still has `hasBra`, and gating the
  // waiting-on-your-agent card on that hid it from exactly the people it is for
  // — anyone past their first application.
  const signatureOutstanding = bra.hasBra && !bra.signed;

  // What still needs the client goes on top; what is finished collapses out of
  // the way. Ordered by created_at desc from the server, a completed listing
  // from last month sat above the purchase they are working on today, and the
  // list grows forever — every finished application pushing the live one down.
  const [showFinished, setShowFinished] = useState(false);
  const { active, finished } = useMemo(() => {
    const isFinished = (d: DossierSummary) =>
      d.status === "COMPLETE" ||
      d.status === "CANCELLED" ||
      d.status === "EXPIRED";
    return {
      active: dossiers.filter((d: DossierSummary) => !isFinished(d)),
      finished: dossiers.filter(isFinished),
    };
  }, [dossiers]);

  // Showings are gated on the AGENT's licences, not on what the client picked
  // today. A mortgage-only agent can never service a viewing whatever the
  // client later decides; a dual-licensed one may well take a "just a loan"
  // client house-hunting, and hiding it from them would be wrong tomorrow.
  const agentQuery = useQuery({
    queryKey: ["onboarding-options", "my-agent"],
    queryFn: () =>
      api.request<{
        is_dual: boolean;
        offered_services: string[];
        sides: { tenant_name: string }[];
      }>("/document/onboarding-options"),
    staleTime: 300_000,
  });
  // Both sides for a dual-licensed agent's client ("Lone Star Realty and
  // Bluebonnet Mortgage"), and a neutral fallback while the query is in flight
  // so the sentence never renders half-written.
  const names = agentQuery.data?.sides?.map((s) => s.tenant_name) ?? [];
  const brokerageName =
    names.length === 0 ? "your brokerage" : names.join(" and ");

  const canRequestShowing =
    agentQuery.data === undefined ||
    agentQuery.data.is_dual ||
    agentQuery.data.offered_services.some((s) => s !== "mortgage");

  if (dossiersQuery.isPending) {
    return (
      <div className="ch-page">
        <p style={{ color: "var(--rp-color-text-muted)" }}>Loading…</p>
      </div>
    );
  }

  return (
    <div className="ch-page">
      <header className="ch-head">
        <div>
          <h1>Your applications</h1>
          {/* Named, because "this brokerage" is vague to someone who may be
              working with two — a dual-licensed agent's client has a real
              estate side and a mortgage side, and `sides` carries both. */}
          <p>Everything you have in motion with {brokerageName}.</p>
        </div>
        <div className="ch-head__actions">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate(`/voice/call/${crypto.randomUUID()}`)}
          >
            <Mic size={15} aria-hidden /> Talk to assistant
          </Button>
          {canRequestShowing && (
            <Button
              size="sm"
              variant="secondary"
              disabled={!bra.loading && !bra.signed}
              title={
                !bra.loading && !bra.signed
                  ? "Sign your Buyer Representation Agreement first"
                  : undefined
              }
              onClick={() => navigate("/appointments/request")}
            >
              <CalendarClock size={15} aria-hidden /> Request a showing
            </Button>
          )}
          <Button size="sm" onClick={() => setShowNewApp(true)}>
            <Plus size={15} aria-hidden /> New
          </Button>
        </div>
      </header>

      {signatureOutstanding && (
        <div
          role="status"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            background: "var(--rp-color-warning-faint, #fff7e6)",
            border: "1px solid var(--rp-color-warning, #b26b00)",
            borderRadius: 10,
            padding: "14px 16px",
            marginBottom: 16,
          }}
        >
          <div>
            <strong>
              Action needed: sign your Buyer Representation Agreement
            </strong>
            <div
              style={{
                fontSize: 13.5,
                color: "var(--rp-color-text-muted)",
                marginTop: 2,
              }}
            >
              Onboarding is almost done — sign the agreement to start requesting
              showings.
            </div>
          </div>
          {bra.formId && (
            <Link to={`/trec/sign/${bra.formId}`}>
              <Button size="sm">Review &amp; sign</Button>
            </Link>
          )}
        </div>
      )}

      {/* Nothing for the client to DO — but silence here reads as a stalled
          application. A seller's whole packet waits while the agent completes
          the listing agreement, so between finishing onboarding and getting the
          signing email this is the only thing that explains the wait. Hidden
          only while a signature is actually outstanding, so it never competes
          with a real call to action. */}
      {!signatureOutstanding && preparing.count > 0 && (
        <div
          role="status"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            background: "var(--rp-color-info-faint, #eef4ff)",
            border: "1px solid var(--rp-color-info, #2b5fd9)",
            borderRadius: 10,
            padding: "14px 16px",
            marginBottom: 16,
          }}
        >
          <div>
            <strong>
              Your agent is preparing your document
              {preparing.count === 1 ? "" : "s"}
            </strong>
            <div
              style={{
                fontSize: 13.5,
                color: "var(--rp-color-text-muted)",
                marginTop: 2,
              }}
            >
              Nothing to do right now — we'll email you as soon as everything is
              ready to sign, and it'll all come in one go.
            </div>
          </div>
        </div>
      )}

      {showNewApp && (
        <NewApplicationPanel onClose={() => setShowNewApp(false)} />
      )}

      {dossiers.length === 0 ? (
        <div className="ch-empty">
          <p>No applications yet — start one above.</p>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <div className="ch-app-list">
              {active.map((d) => (
                <AppRow key={d.id} d={d} />
              ))}
            </div>
          )}

          {finished.length > 0 && (
            <section
              className="ch-done"
              data-open={showFinished ? "true" : "false"}
            >
              <button
                type="button"
                className="ch-done__toggle"
                onClick={() => setShowFinished((v) => !v)}
                aria-expanded={showFinished}
                aria-controls="finished-applications"
              >
                <ChevronDown
                  className="ch-done__chevron"
                  size={20}
                  aria-hidden
                />
                <span className="ch-done__label">Finished</span>
                <span className="ch-done__count">{finished.length}</span>
              </button>
              {showFinished && (
                <div id="finished-applications" className="ch-done__body">
                  {finished.map((d) => (
                    <AppRow key={d.id} d={d} />
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}

      <MyViewingRequests />
      <MyRequests />
    </div>
  );
}
